import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { OutboxRequest } from '@kidshome/api-contract';

/**
 * The offline outbox store (ADR-0018, Phase 7). A small IndexedDB queue of pending mutations the app
 * replays when connectivity returns. Treated as a REBUILDABLE CACHE — the server (with idempotency
 * keys) is the source of truth, so iOS storage eviction can drop it without economy corruption.
 *
 * One record per pending mutation; keyed by the client-generated `idempotencyKey` scoped to the
 * enqueuing member so enqueueing the same intent twice (a React retry, a double-tap) is a no-op
 * rather than a duplicate — while two DIFFERENT members who happen to derive the same deterministic
 * key (e.g. mark-all-read keyed on an announcement's shared `createdAt`) never clobber each other's
 * queued write on a shared tablet. Entries drain FIFO by `createdAt` on reconnect/foreground (see
 * `replay.ts`).
 */

const DB_NAME = 'kidshome-outbox';
const DB_VERSION = 2;
const STORE = 'mutations';

export interface OutboxEntry extends OutboxRequest {
  /**
   * Primary key — `${ownerId ?? 'anon'}:${idempotencyKey}`. Owner-scoped because deterministic
   * idempotency keys (e.g. `notif-readall-${before}`) can be byte-identical across members on a
   * shared device; a bare-key store would let member B's enqueue overwrite member A's queued entry.
   * The unscoped `idempotencyKey` field below is what replay sends as the `Idempotency-Key` header
   * (the server-side store is already user-scoped, so it needs no owner prefix).
   */
  readonly id: string;
  /**
   * The member (user) who enqueued this mutation. The device is SHARED (kid-login "Who's playing?"),
   * so replay must only ever send an entry under the SAME member's session — never under whoever
   * happens to be signed in next (that would apply kid A's redemption to kid B's account, or trip the
   * server's BOLA check and drop A's completed work). `null` means "unattributable" and never replays.
   */
  readonly ownerId: string | null;
  /**
   * The platform-owner impersonation scope (ADR-0055) active when this was enqueued — the
   * `X-Act-As-Household` target, or null when not impersonating. Re-attached on replay so the write
   * lands in the household it was authored in; replay also refuses an entry whose scope no longer
   * matches the live acting-as household (mirrors the `ownerId` gate).
   */
  readonly actAsHouseholdId: string | null;
  /** Epoch ms when first queued; drives FIFO replay order. */
  readonly createdAt: number;
  /** Replay attempts so far (for surfacing/capping; we never infinite-retry). */
  readonly attempts: number;
}

interface OutboxDB extends DBSchema {
  [STORE]: {
    key: string;
    value: OutboxEntry;
    // Index key must be a valid IndexedDB key, so `string` (not `string | null`): entries with a
    // `null` ownerId are simply absent from this index — `countOutboxFor(null)` short-circuits to 0.
    indexes: { 'by-createdAt': number; 'by-ownerId': string };
  };
}

let dbPromise: Promise<IDBPDatabase<OutboxDB>> | null = null;

function db(): Promise<IDBPDatabase<OutboxDB>> {
  dbPromise ??= openDB<OutboxDB>(DB_NAME, DB_VERSION, {
    upgrade(database, oldVersion, _newVersion, transaction) {
      if (oldVersion < 1) {
        const store = database.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('by-createdAt', 'createdAt');
      }
      if (oldVersion < 2) {
        // Add the per-owner index to the EXISTING store (the upgrade transaction) without
        // recreating it, so v1 entries survive the bump — `countOutboxFor` reads this index.
        transaction.objectStore(STORE).createIndex('by-ownerId', 'ownerId');
      }
    },
  });
  return dbPromise;
}

/**
 * Enqueue a pending mutation. Idempotent on `(ownerId, idempotencyKey)`: a re-enqueue under an
 * already-queued key BY THE SAME MEMBER (a React retry, a double-tap) never duplicates or resets the
 * queue position — `createdAt`/`attempts` (the FIFO ordering + retry-budget metadata) stay untouched.
 * Every OTHER field is refreshed from the new `request`, though: the caller's current intent
 * (`method`/`url`/`body`) and the scope it must replay under (`actAsHouseholdId`) must reflect the
 * LATEST attempt, not the first one — otherwise a retry issued after e.g. the platform owner switched
 * acting-as household would silently freeze to the stale first-seen scope, and the replay gate
 * (`outbox-controller.ts`) would skip it forever since it no longer matches the live acting-as
 * household. A DIFFERENT member re-using the same idempotency key gets their own entry (see the
 * `id` docstring). Returns the stored entry.
 */
export async function enqueueOutbox(
  request: OutboxRequest,
  ownerId: string | null,
): Promise<OutboxEntry> {
  const database = await db();
  const id = `${ownerId ?? 'anon'}:${request.idempotencyKey}`;
  const existing = await database.get(STORE, id);
  const entry: OutboxEntry = {
    id,
    method: request.method,
    url: request.url,
    body: request.body,
    idempotencyKey: request.idempotencyKey,
    ownerId,
    actAsHouseholdId: request.actAsHouseholdId,
    createdAt: existing?.createdAt ?? Date.now(),
    attempts: existing?.attempts ?? 0,
  };
  await database.put(STORE, entry);
  return entry;
}

/** All pending entries in FIFO order (oldest first). */
export async function listOutbox(): Promise<OutboxEntry[]> {
  const database = await db();
  return database.getAllFromIndex(STORE, 'by-createdAt');
}

/** Test helper: total number of pending entries (the production badge uses {@link countOutboxFor}). */
export async function __countOutboxForTests(): Promise<number> {
  const database = await db();
  return database.count(STORE);
}

/**
 * Number of pending entries belonging to ONE member (the badge is per-signed-in-user on a shared
 * device — kid B must not see kid A's queued count). Signed out (`null`) → 0.
 */
export async function countOutboxFor(ownerId: string | null): Promise<number> {
  if (ownerId === null) return 0;
  const database = await db();
  return database.countFromIndex(STORE, 'by-ownerId', ownerId);
}

/** Remove an entry by id (after a successful or permanently-failed replay). */
export async function removeOutbox(id: string): Promise<void> {
  const database = await db();
  await database.delete(STORE, id);
}

/** Persist an incremented attempt count for a transient (retryable) failure. Returns the new count. */
export async function bumpAttempts(entry: OutboxEntry): Promise<number> {
  const database = await db();
  const attempts = entry.attempts + 1;
  await database.put(STORE, { ...entry, attempts });
  return attempts;
}

/**
 * Drop every queued entry — ALL owners. Production caller: the right-to-erasure teardown
 * (`use-delete-household`) — erasure wipes the whole household, and queued bodies can carry PII
 * (serialized photo-proof bytes) for members who can never sign in (and thus drain) again. Also used
 * by tests.
 */
export async function clearOutbox(): Promise<void> {
  const database = await db();
  await database.clear(STORE);
}

/** Reset the cached connection (tests open a fresh fake-indexeddb per case). */
export function __resetOutboxDbForTests(): void {
  dbPromise = null;
}
